import React, { useState } from 'react';
import { 
  Activity,
  GitBranch,
  ArrowRight,
  ChevronRight,
  Workflow,
  Server,
  Code,
  AlertTriangle
} from 'lucide-react';

const ProcessViewer = ({ processData }) => {
  const [activeTab, setActiveTab] = useState('diagram');

  // Validation et logging des données
  if (!processData) {
    console.error('ProcessData is undefined');
    return <div className="text-white/70">Données du processus non disponibles</div>;
  }

  console.log('ProcessData reçu:', processData);

  const generateBoxWidth = (title) => {
    const minWidth = 45;
    const titleLength = title.length;
    return Math.max(minWidth, titleLength + 4);
  };

  const generateLine = (width) => '─'.repeat(width);
  
  const generateBox = (title, metadata, isInitial = false, isFinal = false) => {
    const width = generateBoxWidth(title);
    const lines = [];
    
    // Haut de la boîte
    lines.push(`┌${generateLine(width)}┐`);
    
    // Titre avec padding
    const padding = ' '.repeat(Math.max(0, (width - title.length) / 2));
    lines.push(`│${padding}${title}${padding}${padding.length * 2 + title.length < width ? ' ' : ''}│`);
    
    // Séparateur
    if (metadata && Object.keys(metadata).length > 0) {
      lines.push(`│${generateLine(width)}│`);
      // Métadonnées
      Object.entries(metadata).forEach(([key, value]) => {
        const metaText = `• ${key}: ${value}`;
        const metaPadding = ' '.repeat(Math.max(0, width - metaText.length));
        lines.push(`│ ${metaText}${metaPadding}│`);
      });
    }
    
    // Bas de la boîte
    lines.push(`└${generateLine(width)}┘`);
    
    return lines;
  };

  const renderTextDiagram = (process) => {
    if (!process.states) {
      console.error('Process states missing:', process);
      return 'Données du diagramme non disponibles';
    }

    let diagram = [];
    const states = process.states;
    const transitions = process.transitions || [];
    
    states.forEach((state, index) => {
      const stateBox = generateBox(
        `${state.name} [${state.metadata?.timing || 'No timing'}]`,
        state.metadata,
        state.isInitial,
        state.isFinal
      );
      
      diagram.push(...stateBox);
      
      // Ajouter la transition si ce n'est pas le dernier état
      const transition = transitions.find(t => t.from === state.id);
      if (transition && index < states.length - 1) {
        diagram.push('          │');
        diagram.push(`          │ ${transition.name}`);
        diagram.push(`          │ [${transition.roles.join(', ')}]`);
        diagram.push('          ▼');
      }
    });
    
    return diagram.join('\n');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* En-tête */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">{processData.name}</h1>
        <p className="text-white/70">{processData.description}</p>
        <div className="flex gap-2 mt-4">
          {processData.category && (
            <span className="px-2 py-1 text-sm bg-[#017D7E]/10 text-[#017D7E] rounded-full">
              {processData.category}
            </span>
          )}
          {processData.version && (
            <span className="px-2 py-1 text-sm bg-[#017D7E]/10 text-[#017D7E] rounded-full">
              v{processData.version}
            </span>
          )}
        </div>
      </div>

      {/* Onglets */}
      <div className="border-b border-white/10 mb-8">
        <div className="flex space-x-8">
          {[
            { id: 'diagram', label: 'Diagramme', icon: GitBranch },
            { id: 'states', label: 'États', icon: Activity },
            { id: 'transitions', label: 'Transitions', icon: ArrowRight },
            { id: 'rules', label: 'Règles', icon: Code }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-4 relative ${
                activeTab === tab.id
                  ? 'text-[#017D7E]'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.label}</span>
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#017D7E]" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu des onglets */}
      <div className="space-y-6">
        {/* Diagramme */}
        {activeTab === 'diagram' && (
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <pre className="font-mono text-white/90 whitespace-pre text-sm leading-6 overflow-x-auto">
              {renderTextDiagram(processData)}
            </pre>
          </div>
        )}

        {/* États */}
        {activeTab === 'states' && processData.states && (
          <div className="space-y-4">
            {processData.states.map((state) => (
              <div key={state.id} className="bg-white/5 rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg bg-[#017D7E]/10 mt-1`}>
                    <Activity className="w-5 h-5 text-[#017D7E]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{state.name}</h3>
                    <p className="text-white/70 mb-4">{state.description}</p>
                    {state.metadata && (
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {Object.entries(state.metadata).map(([key, value]) => (
                          <div key={key}>
                            <span className="text-white/50">{key}:</span>
                            <span className="ml-2 text-white">
                              {Array.isArray(value) ? value.join(', ') : value.toString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Transitions */}
        {activeTab === 'transitions' && processData.transitions && (
          <div className="space-y-4">
            {processData.transitions.map((transition, idx) => (
              <div key={idx} className="bg-white/5 rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-[#017D7E]/10">
                    <ArrowRight className="w-5 h-5 text-[#017D7E]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {transition.name}
                    </h3>
                    <div className="flex items-center gap-2 text-white/70 mb-4">
                      <span>{transition.from}</span>
                      <ChevronRight className="w-4 h-4" />
                      <span>{transition.to}</span>
                    </div>
                    
                    {transition.conditions && (
                      <div className="mb-4">
                        <h4 className="text-white font-medium mb-2">Conditions</h4>
                        <div className="space-y-2">
                          {transition.conditions.map((condition, i) => (
                            <div key={i} className="flex items-center gap-2 text-white/70">
                              <AlertTriangle className="w-4 h-4 text-yellow-500" />
                              <span>{condition.description}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {transition.actions && (
                      <div>
                        <h4 className="text-white font-medium mb-2">Actions</h4>
                        <div className="space-y-2">
                          {transition.actions.map((action, i) => (
                            <div key={i} className="flex items-center gap-2 text-white/70">
                              <Server className="w-4 h-4 text-[#017D7E]" />
                              <span>{action.description}</span>
                              {action.component && (
                                <span className="text-sm bg-[#017D7E]/10 px-2 py-0.5 rounded">
                                  {action.component}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {transition.roles && (
                      <div className="mt-4">
                        <h4 className="text-white font-medium mb-2">Rôles requis</h4>
                        <div className="flex gap-2">
                          {transition.roles.map((role, i) => (
                            <span key={i} className="text-sm bg-purple-500/10 text-purple-300 px-2 py-0.5 rounded">
                              {role}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Règles */}
        {activeTab === 'rules' && processData.businessRules && (
          <div className="grid grid-cols-1 gap-4">
            {processData.businessRules.map((rule) => (
              <div key={rule.id} className="bg-white/5 rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-[#017D7E]/10">
                    <Code className="w-5 h-5 text-[#017D7E]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-white mb-2">{rule.name}</h3>
                    <p className="text-white/70 mb-3">{rule.description}</p>
                    {rule.implementation && (
                      <div className="bg-black/30 p-3 rounded font-mono text-sm text-white/80">
                        {rule.implementation}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessViewer;